import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { CognitoState } from 'react-cognito';
import { Motion } from 'react-motion';
import Color from 'color';
import get from 'lodash/get';
import history from '../../history';
import { EMPTY_ARRAY, RESPONSIVE_SIZE, PLAN_STATUS, FULL_STYLE } from '../../constants';
import { setSearchText } from '../../actions/search';
import { showSignup, showLogin, logout } from '../../actions/user';
import { springify } from '../../lib/Motion';
import { MyPureComponent } from '../../lib/react';
import Button from '../Button';
import FilterButton from '../FilterButton';
import SearchButton from '../SearchButton';
import { Brandmark, Wordmark, X, Profile, Filters } from '../Icons';
import Link from '../Link';
import MobileNav from '../MobileNav';
import { Hover } from '../Hoverable';
import LicenseMeter from '../LicenseMeter';
import Toggle from '../Toggle';
import TagToggles from '../TagToggles';
import { H3, L2, L3, L4, L4R, P1, P2 } from '../Typography';
import Style from '../Style';

const AUTOSEARCH_TIMEOUT = 750;

const LINK_SHAPE = PropTypes.shape({
  text: PropTypes.string.isRequired,
  href: PropTypes.string,
  onClick: PropTypes.func,
});

class Header extends MyPureComponent {
  static propTypes = {
    responsive: PropTypes.number,
    activeTagIds: PropTypes.arrayOf(PropTypes.number),
    searchText: PropTypes.string,
    brandmarkColor: PropTypes.string,
    brandmarkLink: PropTypes.string,
    crumbs: PropTypes.arrayOf(LINK_SHAPE),
    navLinks: PropTypes.arrayOf(LINK_SHAPE),
    showFilterBar: PropTypes.bool,
    backgroundColor: PropTypes.string,
    setSearchText: PropTypes.func.isRequired,
    profile: PropTypes.object,
    loggedIn: PropTypes.bool,
    doSignup: PropTypes.func,
    doSignin: PropTypes.func,
    searchResultsMessage: PropTypes.string,
    clearTags: PropTypes.func,
    showSignin: PropTypes.bool,
    showSignup: PropTypes.bool,
    showNav: PropTypes.bool,
  };
  static defaultProps = {
    activeTagIds: EMPTY_ARRAY,
    brandmarkColor: Style.Colors.black,
    brandmarkLink: '/',
    crumbs: EMPTY_ARRAY,
    navLinks: EMPTY_ARRAY,
    showFilterBar: false,
    showSignin: true,
    showSignup: true,
    showNav: true,
  };
  static contextTypes = {
    store: PropTypes.object,
  };

  constructor(props) {
    super(props);

    this.state = {
      searchText: props.searchText,
      openFilters: false,
      showMobileNav: false,
    };
    this.autosearchTimeout;
    this.unlisten;
    this.tagsContainer;
  }

  componentDidMount() {
    this.unlisten = history.listen(this.historyChanged);
  }

  componentWillUnmount() {
    this.unlisten();
    document.documentElement.style['maxHeight'] = '';
    document.documentElement.style['overflow'] = '';
  }

  historyChanged = (location, action) => {
    if (action !== 'PUSH') {
      return;
    }

    if (!this.state.searchText) {
      this.searchInput.blur();
      this.props.setSearchText(undefined);
    }
  };

  toggleSearch = e => {
    if (this.props.searchText === undefined) {
      this.props.setSearchText('');
      this.setState({ searchText: '' });
      setTimeout(() => clearTimeout(this.autosearchTimeout), AUTOSEARCH_TIMEOUT * 0.7);
      this.searchInput.focus();
    } else {
      this.searchInput.blur();
      this.props.setSearchText(undefined);
    }
  };

  inputKeyDown = e => {
    if (e.key === 'Escape' && this.props.searchText !== undefined) {
      this.toggleSearch();
    }
  };

  toggleFilters = e => {
    const showFilters = !this.state.openFilters;
    this.setState({ openFilters: showFilters });
    if (showFilters) {
      document.documentElement.style['maxHeight'] = '100vh';
      document.documentElement.style['overflowY'] = 'hidden';
    } else {
      this.tagsContainer.scrollTop = 0;
      document.documentElement.style['maxHeight'] = '';
      document.documentElement.style['overflowY'] = '';
    }
  };

  submitSearch = e => {
    clearTimeout(this.autosearchTimeout);
    e && e.preventDefault && e.preventDefault();
    this.props.setSearchText(this.state.searchText);
  };

  onSearchChange = e => {
    this.setState({ searchText: e.target.value });
    clearTimeout(this.autosearchTimeout);
    this.autosearchTimeout = setTimeout(this.submitSearch, AUTOSEARCH_TIMEOUT);
  };

  onClickX = e => {
    if (this.state.openFilters) {
      return this.toggleFilters();
    } else if (this.state.searchText !== undefined) {
      this.toggleSearch();
    }
  };

  toggleMobileNav = () => {
    this.setState({ showMobileNav: !this.state.showMobileNav });
  };

  render() {
    const {
      responsive,
      activeTagIds,
      searchText,
      brandmarkColor,
      brandmarkLink,
      crumbs,
      navLinks,
      showFilterBar,
      backgroundColor,
      setSearchText,
      profile,
      loggedIn,
      doSignup,
      doSignin,
      searchResultsMessage,
      clearTags,
      showSignin,
      showSignup,
      showNav,
      ...otherProps
    } = this.props;

    const mobileNavMode = responsive < RESPONSIVE_SIZE.TABLET_LANDSCAPE;

    const showFilters = !!this.state.openFilters;
    const showSearchInput = searchText !== undefined && showFilterBar && !showFilters;

    let realCrumbs = crumbs.slice(0, 1);
    if (responsive >= RESPONSIVE_SIZE.TABLET_PORTRAIT) {
      realCrumbs = crumbs;
    }
    if (showSearchInput) {
      realCrumbs = [{ text: '' }];
      if (crumbs.length > 0 && responsive >= RESPONSIVE_SIZE.PHONE) {
        realCrumbs.unshift({
          text: crumbs[0].text,
          onClick: this.toggleSearch,
        });
      }
    }
    if (showFilters) {
      realCrumbs = [{ text: 'Choose Filters', onClick: () => {} }];
      if (crumbs.length > 0 && responsive >= RESPONSIVE_SIZE.PHONE) {
        realCrumbs.unshift({
          text: get(crumbs[0], 'text'),
          onClick: this.toggleFilters,
        });
      }
    }

    const showWordmark =
      responsive < RESPONSIVE_SIZE.TABLET_TABLET &&
      (loggedIn || responsive >= RESPONSIVE_SIZE.PHONE);
    const showNavLinks = showNav && realCrumbs.length < 2 && !showSearchInput && !mobileNavMode;
    const showProfileCard = !showSearchInput && !showFilters && !mobileNavMode;
    const showMobileNav = showNav && !showSearchInput && !showFilters && mobileNavMode;

    const navHeight = mobileNavMode ? 8.5 : 9.3;
    const filterHeight = 9.8;

    let searchIconOnly = true;
    let sidePadding = 2;
    let filterBarIndent = 0;
    let closeShift = 3.9;
    let crumbShift = 1;
    if (responsive >= RESPONSIVE_SIZE.PHONE) {
      searchIconOnly = false;
    }
    if (responsive >= RESPONSIVE_SIZE.TABLET_PORTRAIT) {
      sidePadding = 4;
      filterBarIndent = 4;
      closeShift = 3.9;
      crumbShift = 0;
    }
    if (responsive >= RESPONSIVE_SIZE.TABLET_LANDSCAPE) {
      sidePadding = 7.2;
      filterBarIndent = 7.8;
    }

    const motionStyleDefault = {
      filterTags: 0,
      navLinks: 1,
      xOpacity: 0,
      brandOpacity: 1,
      profileOpacity: 1,
    };
    const motionStyleCurrent = {
      filterTags: showFilters ? 1 : 0,
      navLinks: showNavLinks ? 1 : 0,
      xOpacity: showSearchInput ? 1 : 0,
      brandOpacity: showSearchInput ? 0 : 1,
      profileOpacity: showProfileCard ? 1 : 0,
    };

    const navbarStyle = {
      boxShadow: backgroundColor ? '0 0.1rem 0.5rem 0 rgba(0, 0, 0, 0.14)' : undefined,
      backgroundColor: backgroundColor,
      marginBottom: showFilterBar ? `${filterHeight}rem` : 0,
    };

    let pageYOffset = 0;
    try {
      pageYOffset = window.pageYOffset;
    } catch (err) {}

    return (
      <Motion defaultStyle={motionStyleDefault} style={springify(motionStyleCurrent)}>
        {tweens => {
          return (
            <div>
              <div
                style={{
                  position: 'relative',
                  zIndex: 3,
                  height: `${navHeight}rem`,
                  ...navbarStyle,
                }}
              >
                {!mobileNavMode && (
                  <div
                    style={{
                      opacity: tweens.navLinks,
                      pointerEvents: tweens.navLinks < 0.75 ? 'none' : undefined,
                      height: '100%',
                    }}
                  >
                    <NavLinks navLinks={navLinks} />
                  </div>
                )}

                <div
                  style={{
                    position: 'absolute',
                    top: 0,
                    left: `${sidePadding}rem`,
                    bottom: 0,
                    display: 'flex',
                    alignItems: 'center',
                    pointerEvents: 'none',
                  }}
                >
                  <div
                    style={{
                      opacity: tweens.brandOpacity,
                      pointerEvents: tweens.brandOpacity > 0.75 ? 'auto' : undefined,
                    }}
                  >
                    <Link to={brandmarkLink}>
                      <div style={{ display: 'flex', alignItems: 'center' }}>
                        <Brandmark color={brandmarkColor} style={{ marginBottom: '0.5rem' }} />
                        {showWordmark && (
                          <Wordmark color={brandmarkColor} style={{ margin: '0 1rem' }} />
                        )}
                      </div>
                    </Link>
                  </div>
                  <div
                    style={{
                      position: 'relative',
                      top: '0.3rem',
                      right: `${closeShift}rem`,
                      opacity: tweens.xOpacity,
                      pointerEvents: tweens.xOpacity > 0.75 ? 'auto' : undefined,
                    }}
                  >
                    <Link onClick={this.onClickX}>
                      <X />
                    </Link>
                  </div>

                  <div
                    style={{
                      position: 'relative',
                      left: `-${crumbShift}rem`,
                      top: '0rem',
                      overflow: 'hidden',
                      pointerEvents: showSearchInput || !showNavLinks ? 'auto' : undefined,
                    }}
                  >
                    <Crumbs
                      crumbs={realCrumbs}
                      colorOverride={showSearchInput ? Style.Colors.black : undefined}
                    />
                    <form
                      style={{
                        position: showSearchInput ? 'static' : 'absolute',
                        display: 'inline',
                        opacity: showSearchInput ? 1 : 0,
                        pointerEvents: showSearchInput ? 'auto' : undefined,
                      }}
                      onSubmit={this.submitSearch}
                    >
                      <H3 style={{ display: 'inline' }}>
                        <input
                          onKeyDown={this.inputKeyDown}
                          ref={el => (this.searchInput = el)}
                          style={{
                            padding: 0,
                            border: 'none',
                            color: Style.Colors.shadow3,
                            backgroundColor: 'transparent',
                            opacity: (this.state.searchText || '').length ? 1 : 0.4,
                          }}
                          type="search"
                          onChange={this.onSearchChange}
                          value={this.state.searchText || ''}
                          placeholder="Start Searching..."
                        />
                      </H3>
                    </form>
                  </div>
                </div>

                {showMobileNav && (
                  <div>
                    {this.state.showMobileNav && <MobileNav close={this.toggleMobileNav} />}
                    <div
                      style={{
                        height: '100%',
                        position: 'absolute',
                        zIndex: 1000,
                        top: 0,
                        right: `${sidePadding}rem`,
                        bottom: 0,
                        display: 'flex',
                        alignItems: 'center',
                      }}
                    >
                      <L3
                        onClick={this.toggleMobileNav}
                        style={{
                          marginRight: '1rem',
                          letterSpacing: '0.3rem',
                          color: this.state.showMobileNav ? Style.Colors.white : Style.Colors.black,
                        }}
                      >
                        MENU
                      </L3>
                      <Toggle checked={this.state.showMobileNav} onToggle={this.toggleMobileNav} />
                    </div>
                  </div>
                )}

                {!showMobileNav && (
                  <div
                    style={{
                      opacity: tweens.profileOpacity,
                      pointerEvents: tweens.profileOpacity < 0.75 ? 'none' : undefined,
                    }}
                  >
                    <UserWidget
                      loggedIn={loggedIn}
                      onSignup={doSignup}
                      onSignin={doSignin}
                      showSignin={showSignin}
                      showSignup={showSignup}
                    />
                  </div>
                )}
              </div>

              <div
                style={{
                  position: 'absolute',
                  top: `${navHeight}rem`,
                  width: '100vw',
                  left: 0,
                  zIndex: 2,
                  display: showFilterBar ? 'block' : 'none',
                  height: `${filterHeight}rem`,
                  backgroundColor: backgroundColor,
                }}
                ref={el => (this.tagsContainer = el)}
              >
                <div
                  style={{
                    position: 'absolute',
                    zIndex: 0,
                    paddingLeft: `${sidePadding + filterBarIndent}rem`,
                    top: `-${tweens.filterTags * filterHeight}rem`,
                    top: 0,
                    display: 'flex',
                    flexDirection: 'row',
                    height: `${filterHeight}rem`,
                    alignItems: 'center',
                  }}
                >
                  <SearchButton
                    hover={this.props.searchText !== undefined ? true : undefined}
                    style={{ marginRight: '1.5rem' }}
                    iconOnly={searchIconOnly || this.props.searchText !== undefined}
                    onClick={this.toggleSearch}
                  >
                    Search
                  </SearchButton>
                  <FilterButton
                    hover={activeTagIds.length ? true : undefined}
                    onClick={this.toggleFilters}
                    checked={this.state.showFilters}
                    count={activeTagIds.length}
                  />
                </div>
                <div
                  style={{
                    position: 'fixed',
                    zIndex: 1,
                    width: '100vw',
                    left: 0,
                    top: `calc(${navHeight}rem - ${pageYOffset}px)`,
                    bottom: 0,
                    opacity: tweens.filterTags,
                    pointerEvents: tweens.filterTags < 0.75 ? 'none' : undefined,
                  }}
                >
                  <div
                    style={{
                      paddingTop: '1rem',
                      paddingLeft: `${sidePadding + filterBarIndent}rem`,
                      paddingRight: `${sidePadding}rem`,
                      paddingBottom: '10rem',
                      opacity: 0.97,
                      backgroundColor: backgroundColor,
                      height: '100%',
                      overflowY: 'scroll',
                    }}
                  >
                    <TagToggles />
                  </div>
                  <SeeResults
                    responsive={responsive}
                    message={searchResultsMessage}
                    clearTags={clearTags}
                    onSeeClicked={e => this.toggleFilters()}
                  />
                </div>
              </div>
            </div>
          );
        }}
      </Motion>
    );
  }
}

function mapStateToProps(state) {
  return {
    responsive: state.runtime.responsive,
    activeTagIds: state.search.tags,
    searchText: state.search.text,
    profile: state.user.profile,
    loggedIn: state.cognito.state === CognitoState.LOGGED_IN,
  };
}

function mapDispatchToProps(dispatch) {
  return {
    setSearchText: text => dispatch(setSearchText(text)),
    doSignup: () => dispatch(showSignup()),
    doSignin: () => dispatch(showLogin()),
  };
}

export default connect(mapStateToProps, mapDispatchToProps)(Header);

function UserWidget({ loggedIn, onSignup, onSignin, showSignin, showSignup }) {
  if (loggedIn) {
    return <ProfileWidget />;
  } else {
    return (
      <div
        style={{
          height: '100%',
          position: 'absolute',
          top: 0,
          right: '4rem',
          bottom: 0,
          display: 'flex',
          alignItems: 'center',
        }}
      >
        {showSignin && (
          <Hover>
            <NavLink text="Sign In" onClick={onSignin} />
          </Hover>
        )}

        {showSignup && (
          <Button size="large" onClick={onSignup}>
            Sign Up
          </Button>
        )}
      </div>
    );
  }
}

class ProfileWidgetComp extends MyPureComponent {
  static propTypes = {
    subscription: PropTypes.object,
    pastDue: PropTypes.bool,
    profilePhotoUrl: PropTypes.string,
    onSignOut: PropTypes.func,
  };

  constructor(props) {
    super(props);

    this.state = {
      open: false,
      widthClosed: 9,
      widthOpen: 27,
      heightClosed: 27,
      heightOpen: 53,
    };
  }

  toggleOpen = e => {
    this.setState({ open: !this.state.open });
  };

  checkForClose = e => {
    if (e.target.tagName === 'A') {
      this.close();
    }
  };

  close = e => {
    this.setState({ open: false });
  };

  doSignOut = e => {
    this.setState({ open: false });
    this.props.onSignOut();
    history.push('/');
  };

  render() {
    const { subscription, pastDue, profilePhotoUrl, ...otherProps } = this.props;

    let borderColor = Style.Colors.black;
    let backgroundColor = Style.Colors.shadow4;
    if (pastDue) {
      borderColor = Style.Colors.error;
      backgroundColor = Style.Colors.errorDark;
    }

    const animatedDefault = {
      open: 0,
      outerWidth: this.state.widthClosed,
      outerHeight: this.state.heightClosed,
    };
    const animatedCurrent = {
      open: this.state.open ? 1 : animatedDefault.open,
      outerWidth: this.state.open ? this.state.widthOpen : animatedDefault.outerWidth,
      outerHeight: this.state.open ? this.state.heightOpen : animatedDefault.outerHeight,
    };

    let remaining = 'Unlimited';
    return (
      <Motion defaultStyle={animatedDefault} style={springify(animatedCurrent)}>
        {tween => {
          return (
            <div>
              <div
                style={{
                  ...FULL_STYLE,
                  position: 'fixed',
                  display: this.state.open ? 'block' : 'none',
                }}
                onClick={this.close}
              />
              <div
                style={{
                  position: 'absolute',
                  top: '-20rem',
                  right: 0,
                  height: `${tween.outerHeight}rem`,
                  width: `${tween.outerWidth}rem`,
                  backgroundColor: Color(Style.Colors.white).alpha(tween.open),
                  boxShadow: `0 ${0.2 * tween.open}rem ${2.2 * tween.open}rem 0 ${Color(
                    Style.Colors.shadow5
                  )
                    .alpha(0.79)
                    .string()}`,
                  borderBottomLeftRadius: '0.6rem',
                  overflow: 'hidden',
                }}
                onClick={this.checkForClose}
              >
                <div
                  style={{
                    position: 'absolute',
                    width: `${this.state.widthOpen}rem`,
                    height: `${this.state.heightOpen}rem`,
                    top: 0,
                    right: 0,
                    padding: '22.5rem 2.5rem 0',
                  }}
                >
                  <div
                    style={{
                      display: 'flex',
                    }}
                  >
                    <div
                      style={{
                        opacity: tween.open,
                        flex: '1 1 auto',
                        paddingTop: '0.3rem',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                      }}
                    >
                      <LicenseMeter
                        used={subscription.licensesUsed}
                        max={subscription.licensesMax}
                        style={{ height: '3.9rem', borderColor: borderColor }}
                        textStyle={{ fontWeight: 500, fontSize: '1.8rem' }}
                      />
                      <L4R
                        style={{
                          opacity: pastDue ? 1 : 0.5,
                          margin: '0.6rem auto',
                        }}
                      >
                        {pastDue && (
                          <span style={{ color: Style.Colors.error }}>
                            PAST DUE -{' '}
                            <Link underline={true} to="/dashboard/subscription#payment">
                              UPDATE HERE
                            </Link>
                          </span>
                        )}
                        {!pastDue && <span>LICENSES REMAINING</span>}
                      </L4R>
                    </div>
                    <div>
                      <div
                        onClick={this.toggleOpen}
                        style={{
                          position: 'relative',
                          display: 'flex',
                          justifyContent: 'center',
                          alignItems: 'flex-end',
                          cursor: 'pointer',
                          borderRadius: '99999rem',
                          backgroundColor: backgroundColor,
                          width: '4.2rem',
                          height: '4.2rem',
                          margin: '0 1.5rem',
                          border: `0.3rem solid ${borderColor}`,
                        }}
                      >
                        <Profile color={Style.Colors.white} style={{ maxWidth: '65%' }} />
                        <div
                          style={{
                            ...FULL_STYLE,
                            borderRadius: '99999rem',
                            backgroundImage: `url(${profilePhotoUrl})`,
                            backgroundSize: 'cover',
                            backgroundPosition: 'center',
                          }}
                        />
                      </div>
                    </div>
                  </div>

                  <div
                    style={{
                      opacity: tween.open,
                      padding: '5rem 0',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                    }}
                  >
                    <P1 style={{ margin: '0.5rem 0' }}>
                      <Link to="/browse">Browse</Link>
                    </P1>
                    <P1 style={{ margin: '0.5rem 0' }}>
                      <Link to="/dashboard/licenses">Licensed Clips</Link>
                    </P1>
                    <P1 style={{ margin: '0.5rem 0' }}>
                      <Link to="/dashboard/subscription">My Subscription</Link>
                    </P1>
                    <P2 style={{ margin: '5rem 0', color: Style.Colors.shadow2 }}>
                      <Link onClick={this.doSignOut}>Sign Out</Link>
                    </P2>
                  </div>
                </div>
              </div>
            </div>
          );
        }}
      </Motion>
    );
  }
}

function mapStateToProfileProps(state) {
  return {
    subscription: state.user.subscription,
    pastDue: state.plan.activePlan.status === PLAN_STATUS.PAST_DUE,
    profilePhotoUrl: state.user.profileImageSrc,
  };
}

function mapDispatchToProfileProps(dispatch) {
  return {
    onSignOut: () => dispatch(logout()),
  };
}

const ProfileWidget = connect(mapStateToProfileProps, mapDispatchToProfileProps)(ProfileWidgetComp);

function NavLinks({ hover, navLinks, style }) {
  return (
    <div
      style={{
        height: '100%',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        ...style,
      }}
    >
      {navLinks.map(navLink => (
        <Hover key={navLink.text}>
          <NavLink text={navLink.text} onClick={navLink.onClick} href={navLink.href} />
        </Hover>
      ))}
    </div>
  );
}

function NavLink({ hover, text, onClick, href, ...otherProps }) {
  const baseOpacity = 0.6;
  return (
    <Motion
      defaultStyle={{ opacity: baseOpacity }}
      style={springify({ opacity: hover ? 1 : baseOpacity })}
    >
      {tween => {
        return (
          <L2 {...otherProps}>
            <Link
              style={{
                padding: '0 1.7rem',
                opacity: tween.opacity,
                color: Style.Colors.shadow2,
              }}
              onClick={onClick}
              to={href}
            >
              {text}
            </Link>
          </L2>
        );
      }}
    </Motion>
  );
}

function Crumbs({ crumbs, colorOverride }) {
  const lastIndex = crumbs.length - 1;
  return (
    <div style={{ display: 'inline' }}>
      {crumbs.map(function(crumb, index) {
        const { text, ...otherProps } = crumb;
        const color = index < lastIndex ? Style.Colors.shadow4 : Style.Colors.black;
        const style = {
          color: colorOverride || color,
          display: 'inline',
          textDecoration: 'none',
        };
        return (
          <span key={crumb.text}>
            {!!index && <H3 style={style}>&nbsp;/&nbsp;</H3>}
            <Link style={style} {...otherProps}>
              <H3 style={style}>{text}</H3>
            </Link>
          </span>
        );
      })}
    </div>
  );
}

class SeeResults extends MyPureComponent {
  static propTypes = {
    responsive: PropTypes.number,
    message: PropTypes.string,
    clearTags: PropTypes.func,
    onSeeClicked: PropTypes.func,
  };
  static defaultProps = {
    message: '',
  };

  render() {
    const { responsive, message, clearTags, onSeeClicked, ...otherProps } = this.props;

    let showMessages = false;
    let padding = '0 1rem 0 1rem';
    let minButtonWidth = '22rem';
    let justify = 'flex-start';
    if (responsive >= RESPONSIVE_SIZE.PHONE) {
      showMessages = true;
      padding = '0 12rem 0 2rem';
      justify = 'flex-end';
    }

    return (
      <div
        style={{
          boxShadow: `0 -0.2rem 2.2rem 0 ${Color(Style.Colors.shadow5)
            .alpha(0.79)
            .string()}`,
          zIndex: 9999,
          position: 'fixed',
          width: '100vw',
          height: '9.3rem',
          bottom: '0',
          left: '0',
          backgroundColor: Style.Colors.white,
          display: 'flex',
          justifyContent: justify,
          alignItems: 'center',
          padding: padding,
        }}
      >
        {showMessages && (
          <P1 style={{ fontWeight: 500, marginRight: '3rem', opacity: 0.5 }}>
            {message}
            {clearTags && (
              <span>
                :{' '}
                <Link underline={true} onClick={clearTags}>
                  Clear Filters
                </Link>
              </span>
            )}
          </P1>
        )}

        <Button
          style={{ minWidth: minButtonWidth }}
          size="large"
          hover={true}
          onClick={onSeeClicked}
        >
          {clearTags || !message ? 'Close' : 'See Results'}
        </Button>
      </div>
    );
  }
}



// WEBPACK FOOTER //
// ./src/components/Header/Header.js